import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import CircleImage from "components/common/CircleImage"
import { formatExperienceTermsFull, getMaxGuests } from "utils/experience"
import { formatListPrice, getExperienceCoverImage, getPriceSuffixKey } from "../helpers"
import styles from "./WidgetExperienceCard.module.css"

// The guide's rating as five stars, rounded to whole stars (Figma 6087:173200). None without a rating.
const RATING_STARS = [1, 2, 3, 4, 5]
const GuideRating = ({ rating }) => {
  const { t } = useTranslation()
  const filledStars = Math.round(rating)
  return (
    <Flex gap={2} role="img" aria-label={t("booking_widget.card.rating", { rating: Math.round(rating * 10) / 10 })}>
      {RATING_STARS.map((star) => (
        <Icon
          key={star}
          type="feather"
          name="Star"
          width={14}
          height={14}
          strokeWidth={0}
          fill="currentColor"
          className={star <= filledStars ? styles.starFilled : styles.starEmpty}
        />
      ))}
    </Flex>
  )
}

GuideRating.propTypes = {
  rating: PropTypes.number.isRequired
}

// One experience in the widget's list. The whole card opens the experience; "Book Now" does the
// same, since booking starts from the experience page's availability panel.
const WidgetExperienceCard = ({ experience, guide, onOpen }) => {
  const { t } = useTranslation()
  const coverImage = getExperienceCoverImage(experience)
  const priceSuffixKey = getPriceSuffixKey(experience)
  const maxGuests = getMaxGuests(experience)
  const guideName = guide?.name || ""

  const handleOpen = () => onOpen(experience)
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleOpen()
    }
  }
  const handleBookNow = (event) => {
    event.stopPropagation()
    handleOpen()
  }

  return (
    <div className={styles.card} role="link" tabIndex={0} onClick={handleOpen} onKeyDown={handleKeyDown}>
      <div className={styles.cover}>{coverImage && <img src={coverImage} alt={experience?.title} className={styles.coverImage} />}</div>
      <Flex vertical justify="space-between" className={styles.body}>
        <Flex vertical gap={8}>
          <span className={styles.title}>{experience?.title}</span>
          <Flex vertical gap={4}>
            {/* Duration and guests: two rows, one row on a phone. */}
            <div className={styles.durationAndGuests}>
              <Flex align="center" gap={4} className={styles.infoRow}>
                <Icon type="feather" name="Clock" width={16} height={16} color="currentColor" />
                <span className={styles.infoText}>{formatExperienceTermsFull(experience, t)}</span>
              </Flex>
              {maxGuests > 0 && (
                <Flex align="center" gap={4} className={styles.infoRow}>
                  <Icon type="feather" name="Users" width={16} height={16} color="currentColor" />
                  <span className={styles.infoText}>{t("booking_widget.card.up_to_guests", { count: maxGuests })}</span>
                </Flex>
              )}
            </div>
            {experience?.address && (
              <Flex align="center" gap={4} className={styles.infoRow}>
                <Icon type="feather" name="MapPin" width={16} height={16} color="currentColor" />
                <span className={styles.infoText}>{experience.address}</span>
              </Flex>
            )}
          </Flex>
          {(guideName || guide?.logo) && (
            <Flex align="center" gap={4}>
              <CircleImage src={guide?.logo} alt={guideName} size={28} placeholderIconName="MyProfile" className="flex-shrink-0" />
              <span className={styles.guideName}>{guideName}</span>
              {guide?.rating > 0 && <GuideRating rating={guide.rating} />}
            </Flex>
          )}
        </Flex>
        <Flex align="center" gap={16}>
          <span className={styles.priceBlock}>
            <span className={styles.priceFrom}>{t("booking_widget.card.from")} </span>
            <span className={styles.price}>{formatListPrice(experience?.min_price)}</span>
            {priceSuffixKey && <span className={styles.priceSuffix}> {t(priceSuffixKey)}</span>}
          </span>
          {/* "Book Now", shortened to "Book" on a phone. */}
          <Button type="primary" shape="round" className={styles.bookButton} onClick={handleBookNow}>
            <span className={styles.bookLabelLong}>{t("booking_widget.card.book_now")}</span>
            <span className={styles.bookLabelShort}>{t("booking_widget.card.book")}</span>
          </Button>
        </Flex>
      </Flex>
    </div>
  )
}

WidgetExperienceCard.propTypes = {
  experience: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string,
    address: PropTypes.string,
    max_guests: PropTypes.number,
    ticket_types: PropTypes.arrayOf(PropTypes.shape({ enabled: PropTypes.bool, max_capacity: PropTypes.number })),
    duration_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration_unit: PropTypes.string,
    min_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricing_type: PropTypes.string,
    experience_type: PropTypes.string,
    duration_type: PropTypes.string,
    photos: PropTypes.array,
    cropped_photos: PropTypes.array
  }).isRequired,
  guide: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string,
    rating: PropTypes.number
  }),
  onOpen: PropTypes.func.isRequired
}

WidgetExperienceCard.defaultProps = {
  guide: null
}

export default WidgetExperienceCard
